import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getCountriesExcept, addCountriesExcept, updateCountriesExcept } from "../../redux/features/clicksSlice";
import { Box, Typography, Card, CardContent, Button, Grid, Paper, Switch, TextField, IconButton } from "@mui/material";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';

const CountriesExceptCard = ({ data, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(data);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleToggleActive = async (e) => {
    const val = e.target.checked ? 1 : 0;
    setFormData({ ...formData, Active: val });
    await onSave({ id: data.id, Active: val });
  };

  const handleSave = async () => {
    await onSave(formData);
    setIsEditing(false);
  };

  return (
    <Card variant="outlined" sx={{ mb: 2, borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
      <Box sx={{ p: 1.5, backgroundColor: "#ffffff", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="subtitle1" fontWeight="bold">
          ID: {data.id} | {isEditing ? <TextField size="small" name="table_name" value={formData.table_name} onChange={handleChange} /> : data.table_name}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box display="flex" alignItems="center">
            <Typography variant="body2" mr={1}>Active</Typography>
            <Switch size="small" checked={formData.Active === 1} onChange={handleToggleActive} color="success" />
          </Box>
          {isEditing ? (
            <IconButton color="primary" onClick={handleSave} size="small"><SaveIcon fontSize="small" /></IconButton>
          ) : (
            <IconButton color="primary" onClick={() => setIsEditing(true)} size="small"><EditIcon fontSize="small" /></IconButton>
          )}
        </Box>
      </Box>
      <CardContent sx={{ p: 1.5, backgroundColor: "#fafafa", "&:last-child": { pb: 1.5 } }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="textSecondary">Table ID:</Typography>
            {isEditing ? <TextField fullWidth size="small" name="table_id" value={formData.table_id} onChange={handleChange} /> : <Typography variant="body2">{data.table_id}</Typography>}
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="textSecondary">Start Time:</Typography>
            {isEditing ? <TextField fullWidth size="small" type="time" name="start_time" value={formData.start_time} onChange={handleChange} /> : <Typography variant="body2">{data.start_time}</Typography>}
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="textSecondary">End Time:</Typography>
            {isEditing ? <TextField fullWidth size="small" type="time" name="end_time" value={formData.end_time} onChange={handleChange} /> : <Typography variant="body2">{data.end_time}</Typography>}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">Country Except:</Typography>
            {isEditing ? <TextField fullWidth size="small" multiline name="country_except" value={formData.country_except} onChange={handleChange} /> : <Typography variant="body2">{data.country_except}</Typography>}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const CountriesExcept = () => {
  const dispatch = useDispatch();
  const [dataList, setDataList] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newRow, setNewRow] = useState({ table_id: "", table_name: "", start_time: "00:00:00", end_time: "23:59:59", country_except: "", Active: 1 });

  const fetchData = async () => {
    try {
      const response = await dispatch(getCountriesExcept()).unwrap();
      setDataList(response || []);
    } catch (error) {
      toast.error("Failed to load countries except data");
    }
  };

  useEffect(() => {
    fetchData();
  }, [dispatch]);

  const handleUpdate = async (formData) => {
    try {
      await dispatch(updateCountriesExcept(formData)).unwrap();
      toast.success("Updated successfully!");
      fetchData();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleAdd = async () => {
    try {
      await dispatch(addCountriesExcept(newRow)).unwrap();
      toast.success("Added successfully!");
      setShowAdd(false);
      setNewRow({ table_id: "", table_name: "", start_time: "00:00:00", end_time: "23:59:59", country_except: "", Active: 1 });
      fetchData();
    } catch (e) {
      toast.error("Failed to add");
    }
  };

  return (
    <Box sx={{ padding: { xs: "8px", sm: "16px" }, minHeight: "100vh", backgroundColor: "#f4f7f9" }}>
      <Helmet>
        <title>Alpha Targeting || Countries Except</title>
      </Helmet>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" color="primary.main">
          Countries Except Configurations
        </Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? "Cancel" : "Add New"}
        </Button>
      </Box>

      {showAdd && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: "#fff", borderRadius: "10px" }}>
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>Add New Configuration</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Table Name" value={newRow.table_name} onChange={(e) => setNewRow({...newRow, table_name: e.target.value})} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Table ID" type="number" value={newRow.table_id} onChange={(e) => setNewRow({...newRow, table_id: e.target.value})} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Country Except" value={newRow.country_except} onChange={(e) => setNewRow({...newRow, country_except: e.target.value})} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" label="Start Time" type="time" value={newRow.start_time} onChange={(e) => setNewRow({...newRow, start_time: e.target.value})} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" label="End Time" type="time" value={newRow.end_time} onChange={(e) => setNewRow({...newRow, end_time: e.target.value})} />
            </Grid>
            <Grid item xs={12} sm={4} display="flex" alignItems="center">
              <Typography mr={1}>Active</Typography>
              <Switch checked={newRow.Active === 1} onChange={(e) => setNewRow({...newRow, Active: e.target.checked ? 1 : 0})} />
              <Button variant="contained" color="success" onClick={handleAdd} sx={{ ml: 'auto' }}>Save</Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Grid container spacing={2}>
        {dataList.map(item => (
          <Grid item xs={12} md={6} lg={4} key={item.id}>
            <CountriesExceptCard data={item} onSave={handleUpdate} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CountriesExcept;
